import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface TypingAnimationProps {
  children: string;
  className?: string;
  /** Milliseconds between each character */
  duration?: number;
  /** When true, animation begins on viewport entry rather than immediately */
  startOnView?: boolean;
}

export default function TypingAnimation({
  children,
  className,
  duration = 80,
  startOnView = false,
}: TypingAnimationProps): React.JSX.Element {
  const [displayed, setDisplayed] = useState(startOnView ? '' : children);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  const startTyping = useCallback(() => {
    if (started.current) return;
    started.current = true;
    setDisplayed('');

    let i = 0;
    const tick = () => {
      i += 1;
      setDisplayed(children.slice(0, i));
      if (i < children.length) setTimeout(tick, duration);
    };
    setTimeout(tick, duration);
  }, [children, duration]);

  useEffect(() => {
    if (!startOnView) {
      startTyping();
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startTyping();
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [startOnView, startTyping]);

  const isComplete = displayed.length >= children.length;

  return (
    <span ref={ref} className={className}>
      {displayed}
      {!isComplete && (
        <span
          aria-hidden="true"
          style={{
            borderRight: '2px solid currentColor',
            marginLeft: 1,
            animation: 'typing-blink 0.7s step-end infinite',
          }}
        />
      )}
    </span>
  );
}
